import { error as logError } from '@/utils/debugLogger';
import feedbackService from '@Feedback/service/feedback.service';
import {
  FEEDBACK_SESSION_KEY,
  FEEDBACK_LOW_RATING_THRESHOLD,
} from '@Feedback/constants/feedbackConstants';

/**
 * User Feedback prompt state (AVA-349).
 *
 * The cadence lives entirely on the server. This module owns three things only:
 * whether the prompt is on screen, whether a write is in flight, and the
 * per-browser-session guard that stops a second prompt inside one session.
 */

/**
 * The guard is keyed per user, not per tab alone. A bare key would survive a
 * logout/login in the same tab, so the second person at a shared workstation
 * would be silently skipped — the one user most likely to be a field engineer
 * borrowing a colleague's machine.
 */
function sessionKeyFor(userId) {
  return `${FEEDBACK_SESSION_KEY}.${userId || 'anonymous'}`;
}

/**
 * Has this browser session already RESOLVED eligibility for this user?
 * sessionStorage throws in some privacy modes, so every access is guarded — a
 * storage failure must never stop the feature, only weaken the guard.
 */
function hasCheckedThisSession(userId) {
  try {
    return !!window.sessionStorage.getItem(sessionKeyFor(userId));
  } catch (err) {
    logError('FEEDBACK: sessionStorage read failed', err);
    return false;
  }
}

/** Stores the ISO instant rather than a flag: a timestamp is debuggable. */
function markCheckedThisSession(userId) {
  try {
    window.sessionStorage.setItem(sessionKeyFor(userId), new Date().toISOString());
  } catch (err) {
    logError('FEEDBACK: sessionStorage write failed', err);
  }
}

export const FEEDBACK = {
  namespaced: true,

  state: {
    visible: false,
    /** A write (submit or snooze) is in flight. */
    busy: false,
    /** Set when a submit fails; cleared on retry. Snooze never surfaces this — see SNOOZE. */
    submitFailed: false,
    /** null | 'success' | 'snoozed' — which terminal panel to show. */
    result: null,
    /** The rating that was submitted, so the terminal copy can acknowledge a low score. */
    submittedRating: null,
    /** Guards against a second eligibility call while one is already running. */
    checking: false,
  },

  getters: {
    /** A 1–2 star rater gets acknowledgement copy instead of a generic thanks. */
    IS_LOW_RATING(state) {
      return (
        state.submittedRating !== null &&
        state.submittedRating <= FEEDBACK_LOW_RATING_THRESHOLD
      );
    },
  },

  mutations: {
    SET_VISIBLE(state, value) {
      state.visible = value;
    },
    SET_BUSY(state, value) {
      state.busy = value;
    },
    SET_SUBMIT_FAILED(state, value) {
      state.submitFailed = value;
    },
    SET_RESULT(state, value) {
      state.result = value;
    },
    SET_SUBMITTED_RATING(state, value) {
      state.submittedRating = value;
    },
    SET_CHECKING(state, value) {
      state.checking = value;
    },
    RESET(state) {
      state.visible = false;
      state.busy = false;
      state.submitFailed = false;
      state.result = null;
      state.submittedRating = null;
    },
  },

  actions: {
    /**
     * Ask the server whether to prompt, at most once per browser session.
     * Called from the auth watcher in App.vue, which covers all three session
     * starts: app load with a token, credential login (full reload), and the
     * SSO callback (a client-side route change that never remounts App).
     */
    async CHECK_ELIGIBILITY({ commit, state, rootGetters }) {
      // Never prompt while impersonating: the submission would be written against
      // the impersonated user, putting words in a customer's mouth.
      if (rootGetters['ACCOUNT/IS_IMPERSONATE_MODE']) return;

      const user = rootGetters['ACCOUNT/GET_USER'];
      const userId = user && user.id;
      if (state.checking || state.visible || hasCheckedThisSession(userId)) return;

      commit('SET_CHECKING', true);
      try {
        const res = await feedbackService.getEligibility();
        if (res.data && res.data.shouldPrompt) {
          commit('RESET');
          commit('SET_VISIBLE', true);
        }
      } catch (err) {
        // Eligibility is not worth bothering the user about: if we cannot ask,
        // we simply do not prompt this session.
        logError('FEEDBACK: eligibility check failed', err);
      } finally {
        // Marked once the check has RESOLVED, whatever the answer - not only when
        // the answer was "prompt". Marking on the prompt alone left every
        // ineligible user (the large majority) re-hitting the endpoint on every
        // route change for the whole session. Spec: "call eligibility once".
        markCheckedThisSession(userId);
        commit('SET_CHECKING', false);
      }
    },

    /**
     * Submit a rating. On failure the dialog stays open with the comment intact
     * so the user can retry — losing a typed comment to a network blip is the
     * one unrecoverable outcome here.
     */
    async SUBMIT({ commit }, { rating, comment }) {
      commit('SET_BUSY', true);
      commit('SET_SUBMIT_FAILED', false);
      try {
        await feedbackService.submit({ rating, comment: comment || null });
        commit('SET_SUBMITTED_RATING', rating);
        commit('SET_RESULT', 'success');
        return true;
      } catch (err) {
        logError('FEEDBACK: submit failed', err);
        commit('SET_SUBMIT_FAILED', true);
        return false;
      } finally {
        commit('SET_BUSY', false);
      }
    },

    /**
     * Snooze for a week. Backs the X, Esc, outside-click and the text action.
     *
     * Deliberately fire-and-forget for the USER, but never for the SESSION: if
     * the POST fails we still close and still keep the session guard set, so a
     * user on a bad connection is not re-prompted in a loop. What we must not do
     * is claim success we did not get — hence no confirmation copy on failure.
     */
    async SNOOZE({ commit, rootGetters }) {
      const user = rootGetters['ACCOUNT/GET_USER'];
      markCheckedThisSession(user && user.id);
      commit('SET_BUSY', true);
      try {
        await feedbackService.snooze();
        commit('SET_RESULT', 'snoozed');
      } catch (err) {
        // The server did not record the snooze. Close anyway (the session guard
        // covers this session) but show nothing that implies it was stored.
        logError('FEEDBACK: snooze failed', err);
        commit('SET_VISIBLE', false);
      } finally {
        commit('SET_BUSY', false);
      }
    },

    DISMISS({ commit }) {
      commit('RESET');
    },
  },
};

export default FEEDBACK;
